import {
  getCurrencySymbol,
  isUserFromAustralia,
  isUserFromNewZealand,
  isUserFromUK,
} from 'src/lib/utils';
import Product from 'src/models/Product';
import { getChallengePlans, PLANS, PlanType } from 'src/pages/constants';
import { Plan, Plans } from 'src/pages/deprecated/payment/types';
import { ChallengePlan, ChallengeSelectionPreviewItem } from 'src/pages/types';
import { ChallengeAssetsMetadata } from 'src/store/signup-workflow/types';
import moment from 'moment';

export const sortSubscriptionPlans = (plans: Plans) => {
  const getSortKey = (plan: Plan) => {
    const isChallenge = plan.metadata?.challenge === 'true';
    const isStandard = plan.id.includes(PLANS.STANDARD_PLAN);
    const isWeekly = plan.interval === 'week';

    // Priority logic:
    // Lower number = higher priority
    return {
      priority: isChallenge ? 0 : isStandard ? 1 : 2,
      intervalPriority: isWeekly ? 0 : 1,
      intervalCount: plan.intervalCount || 0,
    };
  };

  return Object.values(plans).sort((a, b) => {
    const aKey = getSortKey(a);
    const bKey = getSortKey(b);

    return (
      aKey.priority - bKey.priority ||
      aKey.intervalPriority - bKey.intervalPriority ||
      aKey.intervalCount - bKey.intervalCount
    );
  });
};

export const extractPlans = (
  currentPlans: Plans,
  productData: Product
): Plans => {
  return Object.values(productData.prices).reduce<Plans>(
    (accumulator, price) => {
      if (!price.active) return accumulator;

      const isRecurring = !!price.recurring;
      const planName =
        price.metadata?.planName || productData.name || productData.id;

      // Create base plan entry if it doesn't exist
      if (!accumulator[planName]) {
        accumulator[planName] = {
          id: planName,
          interval: isRecurring ? price.recurring.interval : 'once',
          intervalCount: isRecurring ? price.recurring.intervalCount : 1,
          mealPlanVariants: {},
          active: true,
          metadata: productData.metadata || {},
          currency_options: price.currency_options || {},
        };
      }

      const weeklyMealPlanQty = isRecurring
        ? price.recurring.weeklyMealPlanQty
        : 1; // fallback for one-time challenge products

      const variantKey = weeklyMealPlanQty;

      // Add variant to plan
      accumulator[planName].mealPlanVariants[variantKey || 1] = {
        id: price.id,
        productId: productData.id,
        unitAmount: price.unitAmount,
        unitAmountCents: price.unitAmountCents,
        unitAmountPerDay: price.unitAmountPerDay,
        unitAmountPerWeek: price.unitAmountPerWeek,
        weeklyMealPlanQty,
        weeksPurchased: price.weeksPurchased ?? null,
        metadata: price.metadata,
      };

      return accumulator;
    },
    currentPlans
  );
};

export const mergeChallengePlans = (
  allPlansSorted: Plan[],
  challengeStartDate?: string,
  assetMetadata?: ChallengeAssetsMetadata
): ChallengePlan[] => {
  const isAUS = isUserFromAustralia();

  // Use the challenge start date or empty string if not available
  const startDate = challengeStartDate || '';
  const challengePlans = getChallengePlans(startDate, assetMetadata);

  // Filter out supplements plan for non-Australian users
  const availableChallengePlans = isAUS
    ? challengePlans
    : challengePlans.filter(
        plan => plan.id !== PLANS.CHALLENGE_WITH_SUPPLEMENTS
      );

  return availableChallengePlans.map(constantPlan => {
    const matchedApiPlan = allPlansSorted.find(
      apiPlan => apiPlan.id === constantPlan.id
    );

    let price = Number(matchedApiPlan?.mealPlanVariants?.[1]?.unitAmount ?? '');
    price = isNaN(price) ? 0 : price;

    return {
      ...constantPlan,
      price,
      plan: matchedApiPlan || null,
    };
  });
};

export const getPersonalCoachPricing = (allPlansSorted: Plan[]): string => {
  const personalCoachPlan = allPlansSorted.find(
    plan => plan.id === PLANS.PERSONAL_COACH
  );

  if (!personalCoachPlan) {
    return '';
  }

  let price = Number(
    personalCoachPlan?.mealPlanVariants?.[1]?.unitAmount ?? ''
  );
  price = isNaN(price) ? 0 : price;

  return price.toFixed(2);
};

export const getSupplementPricing = (allPlansSorted: Plan[]): string => {
  const supplementPlan = allPlansSorted.find(
    plan => plan.id === PLANS.SUPPLEMENT_PACK
  );

  if (!supplementPlan) {
    return '';
  }

  let price = Number(supplementPlan?.mealPlanVariants?.[1]?.unitAmount ?? '');
  price = isNaN(price) ? 0 : price;

  return price.toFixed(2);
};

// Get Nutrition Coaching Addon (Normal Subscription) Pricing
export const getNutritionCoachingPricing = (allPlansSorted: Plan[]): string => {
  const nutritionCoachingPlan = allPlansSorted.find(
    plan => plan.id === PLANS.NUTRITION_COACHING
  );

  if (!nutritionCoachingPlan) {
    return '';
  }

  let price = Number(
    nutritionCoachingPlan?.mealPlanVariants?.[1]?.unitAmount ?? ''
  );
  price = isNaN(price) ? 0 : price;

  return price.toFixed(2);
};

export const getPersonalCoachPriceId = (allPlansSorted: Plan[]): string => {
  const personalCoachPlan = allPlansSorted.find(
    plan => plan.id === PLANS.PERSONAL_COACH
  );

  return personalCoachPlan?.mealPlanVariants?.[1]?.id ?? '';
};

export const getNutritionCoachingPriceId = (allPlansSorted: Plan[]): string => {
  const nutritionCoachingPlan = allPlansSorted.find(
    plan => plan.id === PLANS.NUTRITION_COACHING
  );

  return nutritionCoachingPlan?.mealPlanVariants?.[1]?.id ?? '';
};

export const getSupplementPackPriceId = (allPlansSorted: Plan[]): string => {
  const supplementPackPlan = allPlansSorted.find(
    plan => plan.id === PLANS.SUPPLEMENT_PACK
  );

  return supplementPackPlan?.mealPlanVariants?.[1]?.id ?? '';
};

export function getDefaultPlanByCountry(plans: Plan[]): {
  planId: string | null;
  planType: PlanType;
} {
  const getSecondLast = (list: Plan[]) => {
    if (!list.length) return null;
    return list.length >= 2 ? list[list.length - 2] : list[list.length - 1];
  };

  const standardPlans = plans.filter(p => p.id.includes('Standard'));
  const standardPlan = getSecondLast(standardPlans);

  // Always return Standard plan - Premium plans are no longer available
  return { planId: standardPlan?.id ?? null, planType: 'standard' };
}

/**
 * Returns the 3-month Standard plan for use as the reactivation default.
 * Matches by intervalCount=3 first, then falls back to a name-based match,
 * then to the first available standard plan.
 */
export function getThreeMonthStandardPlan(plans: Plan[]): {
  planId: string | null;
  planType: PlanType;
} {
  const standardPlans = plans.filter(p => p.id.includes('Standard'));

  // 1. Prefer exact 3-month interval match
  const threeMonth = standardPlans.find(p => p.intervalCount === 3);
  if (threeMonth) return { planId: threeMonth.id, planType: 'standard' };

  // 2. Fallback: name contains "3 Month"
  const nameMatch = standardPlans.find(p => p.id.includes('3 Month'));
  if (nameMatch) return { planId: nameMatch.id, planType: 'standard' };

  // 3. Last resort: first available standard plan
  const first = standardPlans[0];
  return { planId: first?.id ?? null, planType: 'standard' };
}

export const getMonthlyPricingDisplay = (): string => {
  const isUK = isUserFromUK();
  const isNewZealand = isUserFromNewZealand();

  let price = '';

  if (isUK) {
    price = '34';
  } else if (isNewZealand) {
    price = '79';
  } else {
    // Default pricing like Australia
    price = '69';
  }

  return getCurrencySymbol() + price;
};

export const formatPricingAmount = (price: number | string): string => {
  const amount = Number(price || 0);

  const formattedAmount = Number.isInteger(amount)
    ? amount.toString()
    : amount.toFixed(2);

  return formattedAmount;
};

export const getChallengeIntervalString = (
  planId: string
): string | undefined => {
  switch (planId) {
    case PLANS.CHALLENGE_WITH_6_MONTH_PLAN:
      return '6 months';
    case PLANS.CHALLENGE_WITH_3_MONTH_PLAN:
      return '3 months';
    default:
      return undefined;
  }
};

export const getPlanPricing = (
  matchedApiPlan?: Plan | null,
  fallbackPrice: number = 0,
  fallbackOriginalPrice?: number
): { price: number; originalPrice?: number } => {
  let price = Number(matchedApiPlan?.mealPlanVariants?.[1]?.unitAmount ?? '');
  price = isNaN(price) || price === 0 ? fallbackPrice : price;

  // Only fall back to the hardcoded "was" price when there's no matched
  // Stripe plan at all. If a plan matched but its metadata has no
  // orgPrice/originalPrice, the backend isn't signalling a discount (e.g.
  // the discount window has ended) - don't show a stale strikethrough.
  let originalPrice = matchedApiPlan ? undefined : fallbackOriginalPrice;
  const priceMetadata = matchedApiPlan?.mealPlanVariants?.[1]?.metadata;
  if (priceMetadata?.orgPrice) {
    const parsedOriginalPrice = Number(priceMetadata.orgPrice);
    if (!isNaN(parsedOriginalPrice)) {
      originalPrice = parsedOriginalPrice;
    }
  } else if (matchedApiPlan?.metadata?.originalPrice) {
    const parsedOriginalPrice = Number(matchedApiPlan.metadata.originalPrice);
    if (!isNaN(parsedOriginalPrice)) {
      originalPrice = parsedOriginalPrice;
    }
  }

  return { price, originalPrice };
};

export const formatChallengePreviewLine = (
  item: ChallengeSelectionPreviewItem
): string => {
  const currencySymbol = getCurrencySymbol();
  const formattedPrice = formatPricingAmount(item.nextPlan);

  let periodStr = 'month';
  if (item.scheduledMonths && item.scheduledMonths > 0) {
    periodStr = `${item.scheduledMonths} ${
      item.scheduledMonths === 1 ? 'month' : 'months'
    }`;
  }

  const formattedDate = item.scheduleStart
    ? moment(item.scheduleStart).isValid()
      ? moment(item.scheduleStart).format('MMMM D, YYYY')
      : item.scheduleStart
    : '';

  return `Then billed at ${currencySymbol}${formattedPrice}/${periodStr} from ${formattedDate}. Your subscription will automatically renew until cancelled.`;
};

export const getChallengeSelectionPreviewLine = (
  previewList: ChallengeSelectionPreviewItem[] | null | undefined,
  priceId?: string,
  selectedPlanId?: string,
  challengePackageSelectedPlan?: ChallengePlan | null
): string | undefined => {
  if (!previewList || !previewList.length) return undefined;

  const possiblePriceIds = new Set<string>();
  if (priceId) possiblePriceIds.add(priceId);
  if (selectedPlanId) possiblePriceIds.add(selectedPlanId);

  if (challengePackageSelectedPlan) {
    if (challengePackageSelectedPlan.id) {
      possiblePriceIds.add(challengePackageSelectedPlan.id);
    }
    if (challengePackageSelectedPlan.checkoutPlanId) {
      possiblePriceIds.add(challengePackageSelectedPlan.checkoutPlanId);
    }
    if (challengePackageSelectedPlan.plan?.id) {
      possiblePriceIds.add(challengePackageSelectedPlan.plan.id);
    }
  }

  const matchingItem = previewList.find(
    item =>
      (item.currentChallengePriceId &&
        possiblePriceIds.has(item.currentChallengePriceId)) ||
      (item.challengePlanType && possiblePriceIds.has(item.challengePlanType))
  );

  if (!matchingItem) return undefined;

  return formatChallengePreviewLine(matchingItem);
};

export const findChallengePlan = (
  plans: ChallengePlan[],
  targetId?: string | null
): ChallengePlan | null => {
  if (!targetId || targetId === 'true') return null;
  const cleanTarget = targetId.trim().toLowerCase();

  return (
    plans.find(p => p.localId && p.localId.toLowerCase() === cleanTarget) ||
    plans.find(p => p.id && p.id.toLowerCase() === cleanTarget) ||
    plans.find(
      p => p.checkoutPlanId && p.checkoutPlanId.toLowerCase() === cleanTarget
    ) ||
    plans.find(p => p.plan?.id && p.plan.id.toLowerCase() === cleanTarget) ||
    plans.find(
      p =>
        p.plan?.mealPlanVariants?.[1]?.id &&
        p.plan.mealPlanVariants[1].id.toLowerCase() === cleanTarget
    ) ||
    plans.find(p => p.id && p.id.toLowerCase().includes(cleanTarget)) ||
    null
  );
};
